import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Filter, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

interface Submission {
  id: string;
  title: string;
  description: string;
  status: string;
  submission_type: string;
  submitted_by_user_id: string;
  submitted_by_user_name: string;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  'DRAFT': 'bg-gray-100 text-gray-800',
  'SUBMITTED': 'bg-blue-100 text-blue-800',
  'APPROVAL_1_PENDING': 'bg-yellow-100 text-yellow-800',
  'APPROVAL_1_APPROVED': 'bg-green-100 text-green-800',
  'PACKAGING': 'bg-purple-100 text-purple-800',
  'APPROVAL_2_PENDING': 'bg-yellow-100 text-yellow-800',
  'APPROVAL_2_APPROVED': 'bg-green-100 text-green-800',
  'REJECTED': 'bg-red-100 text-red-800',
  'POSTED': 'bg-indigo-100 text-indigo-800',
  'METRICS_SYNCED': 'bg-teal-100 text-teal-800',
};

const SubmissionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [mineFilter, setMineFilter] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchCount();
  }, [page, statusFilter, mineFilter]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      setPage(1);
      fetchSubmissions();
      fetchCount();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params: any = { page, page_size: pageSize };
      if (searchQuery) params.q = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (mineFilter) params.mine = true;
      
      const response = await axios.get(`${API}/submissions`, { params });
      let filteredSubmissions = response.data;
      
      // Filter based on role and status
      const isMarketingExecutive = user?.roles?.includes('Marketing Executive');
      const isCreativeAgency = user?.roles?.includes('Creative Agency');
      const isMarketingHead = user?.roles?.includes('Marketing Head');
      const isAdmin = user?.roles?.includes('Admin');
      
      filteredSubmissions = filteredSubmissions.filter((sub: Submission) => {
        // Remove POSTED from all users (shown in Posts page instead)
        if (sub.status === 'POSTED') return false;
        
        // Creative Agency should NOT see OTHER users' DIRECT submissions
        // But they CAN see their own Direct submissions
        if (isCreativeAgency && sub.submission_type === 'direct_to_head') {
          const isOwner = sub.submitted_by_user_id === user?.id;
          if (!isOwner) {
            return false; // Hide other users' Direct submissions
          }
          // Allow their own Direct submissions to be visible
        }
        
        // DRAFT submissions: Only show own drafts for Exec/Agency, all for Admin, none for Head
        if (sub.status === 'DRAFT') {
          if (isAdmin) {
            return true; // Admin sees all drafts
          }
          if (isMarketingHead) {
            return false; // Marketing Head does NOT see drafts (not submitted yet)
          }
          // Marketing Executive and Creative Agency only see their own drafts
          return (isMarketingExecutive || isCreativeAgency) && sub.submitted_by_user_id === user?.id;
        }
        
        return true;
      });
      
      setSubmissions(filteredSubmissions);
      
      // Update count based on filtered submissions (since we filter client-side)
      // We need to fetch total count separately with same filters
      await fetchFilteredCount();
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredCount = async () => {
    try {
      const params: any = {};
      if (searchQuery) params.q = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (mineFilter) params.mine = true;
      
      // Fetch all submissions to apply same filtering logic
      const response = await axios.get(`${API}/submissions`, { 
        params: { ...params, page: 1, page_size: 10000 } // Large page size to get all
      });
      
      let allSubmissions = response.data;
      const isMarketingExecutive = user?.roles?.includes('Marketing Executive');
      const isCreativeAgency = user?.roles?.includes('Creative Agency');
      const isMarketingHead = user?.roles?.includes('Marketing Head');
      const isAdmin = user?.roles?.includes('Admin');
      
      // Apply same filtering logic as fetchSubmissions
      allSubmissions = allSubmissions.filter((sub: Submission) => {
        if (sub.status === 'POSTED') return false;
        if (isCreativeAgency && sub.submission_type === 'direct_to_head') {
          const isOwner = sub.submitted_by_user_id === user?.id;
          if (!isOwner) {
            return false; // Hide other users' Direct submissions
          }
        }
        if (sub.status === 'DRAFT') {
          if (isAdmin) return true;
          if (isMarketingHead) return false;
          return (isMarketingExecutive || isCreativeAgency) && sub.submitted_by_user_id === user?.id;
        }
        return true;
      });
      
      setTotalCount(allSubmissions.length);
    } catch (error) {
      console.error('Failed to fetch count:', error);
    }
  };

  const fetchCount = fetchFilteredCount;

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  return (
    <div data-testid="submissions-list-page">
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 mb-4 lg:mb-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
              Submissions
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage marketing content submissions</p>
          </div>
          {(user?.roles?.includes('Marketing Executive') || user?.roles?.includes('Creative Agency') || user?.roles?.includes('Admin')) && (
            <button
              onClick={() => navigate('/submissions/new')}
              className="w-full sm:w-auto px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              data-testid="create-submission-button"
            >
              + Create New
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="APPROVAL_1_PENDING">Approval 1 Pending</option>
            <option value="APPROVAL_1_APPROVED">Approval 1 Approved</option>
            <option value="PACKAGING">Packaging</option>
            <option value="APPROVAL_2_PENDING">Approval 2 Pending</option>
            <option value="APPROVAL_2_APPROVED">Approval 2 Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="POSTED">Posted</option>
            <option value="METRICS_SYNCED">Metrics Synced</option>
          </select>

          <label className="flex items-center space-x-2 px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={mineFilter}
              onChange={(e) => setMineFilter(e.target.checked)}
              className="rounded"
              data-testid="mine-filter"
            />
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">My Submissions</span>
          </label>
        </div>
      </div>

      {/* Submissions Table/Cards */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <FileText className="mx-auto mb-2 text-gray-400" size={32} />
            <p>No submissions found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {submissions.map((submission) => (
                    <tr
                      key={submission.id}
                      className="hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => navigate(`/submissions/${submission.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{submission.title}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{submission.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[submission.status] || 'bg-gray-100 text-gray-800'}`}>
                          {formatStatus(submission.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {submission.submitted_by_user_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/submissions/${submission.id}`);
                          }}
                          className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  onClick={() => navigate(`/submissions/${submission.id}`)}
                  className="p-4 hover:bg-gray-50 transition cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900 flex-1 pr-2">{submission.title}</h3>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[submission.status] || 'bg-gray-100 text-gray-800'}`}>
                      {formatStatus(submission.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{submission.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{submission.submitted_by_user_name}</span>
                    <span>{new Date(submission.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
              Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> submissions
            </div>
            <div className="flex items-center space-x-2 order-1 sm:order-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center px-3 text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsListPage;
